import React from 'react';
import { ArrowLeftIcon } from 'lucide-react';
type PageType = 'home' | 'knowledge' | 'tools' | 'research' | 'dental-kids' | 'logo';
interface LogoShowcasePageProps {
  onNavigate: (page: PageType) => void;
}
interface DentosLogoProps {
  variant: 1 | 2 | 3 | 4;
  layout: 'horizontal' | 'stacked';
  width: number;
  showTagline?: boolean;
}
const variantColors = {
  1: {
    bg: '#0F1E3C',
    bars: '#00C2D4',
    text: '#FFFFFF',
    border: 'none'
  },
  2: {
    bg: '#FFFFFF',
    bars: '#00C2D4',
    text: '#0F1E3C',
    border: '#E5E7EB'
  },
  3: {
    bg: '#00C2D4',
    bars: '#FFFFFF',
    text: '#FFFFFF',
    border: 'none'
  },
  4: {
    bg: 'transparent',
    bars: '#000000',
    text: '#000000',
    border: 'none'
  }
};
function LogoSymbol({
  color,
  size
}: {
  color: string;
  size: number;
}) {
  const scale = size / 40;
  return <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-567">
      {/* Left vertical stroke (the stem of D) */}
      <rect x="8" y="8" width="4" height="24" rx="2" fill={color} data-id="element-568" />
      {/* Three ascending bars forming the curve of D / dental arch / chart */}
      <rect x="14" y="18" width="4" height="14" rx="2" fill={color} data-id="element-569" />
      <rect x="20" y="13" width="4" height="19" rx="2" fill={color} data-id="element-570" />
      <rect x="26" y="8" width="4" height="24" rx="2" fill={color} data-id="element-571" />
    </svg>;
}
function DentosLogo({
  variant,
  layout,
  width,
  showTagline = false
}: DentosLogoProps) {
  const colors = variantColors[variant];
  const isHorizontal = layout === 'horizontal';
  // Calculate sizes based on width
  const symbolSize = isHorizontal ? Math.round(width * 0.18) : Math.round(width * 0.35);
  const fontSize = isHorizontal ? Math.round(width * 0.08) : Math.round(width * 0.12);
  const letterSpacing = Math.max(1, Math.round(fontSize * 0.15));
  const taglineFontSize = Math.round(fontSize * 0.45);
  const padding = Math.round(symbolSize * 0.3);
  const gap = Math.round(symbolSize * 0.25);
  if (isHorizontal) {
    return <div className="inline-flex items-center rounded-xl" style={{
      backgroundColor: colors.bg,
      border: colors.border !== 'none' ? `1px solid ${colors.border}` : 'none',
      padding: `${padding}px ${padding * 1.5}px`,
      gap: `${gap}px`
    }} data-id="element-572">
        <LogoSymbol color={colors.bars} size={symbolSize} data-id="element-573" />
        <span style={{
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 800,
        fontSize: `${fontSize}px`,
        letterSpacing: `${letterSpacing}px`,
        color: colors.text
      }} data-id="element-574">
          DENTOS
        </span>
      </div>;
  }
  // Stacked layout
  return <div className="inline-flex flex-col items-center rounded-xl" style={{
    backgroundColor: colors.bg,
    border: colors.border !== 'none' ? `1px solid ${colors.border}` : 'none',
    padding: `${padding * 1.2}px ${padding * 2}px`,
    gap: `${gap * 0.6}px`
  }} data-id="element-575">
      <LogoSymbol color={colors.bars} size={symbolSize} data-id="element-576" />
      <span style={{
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: 800,
      fontSize: `${fontSize}px`,
      letterSpacing: `${letterSpacing}px`,
      color: colors.text
    }} data-id="element-577">
        DENTOS
      </span>
      {showTagline && <span style={{
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: 400,
      fontSize: `${taglineFontSize}px`,
      color: colors.text,
      opacity: 0.7,
      marginTop: `${gap * 0.2}px`
    }} data-id="element-578">
          Знания которые строят бизнес
        </span>}
    </div>;
}
function SymbolOnly({
  variant,
  size
}: {
  variant: 1 | 2 | 3 | 4;
  size: number;
}) {
  const colors = variantColors[variant];
  const padding = Math.round(size * 0.15);
  return <div className="inline-flex items-center justify-center rounded-lg" style={{
    backgroundColor: colors.bg,
    border: colors.border !== 'none' ? `1px solid ${colors.border}` : 'none',
    padding: `${padding}px`
  }} data-id="element-579">
      <LogoSymbol color={colors.bars} size={size - padding * 2} data-id="element-580" />
    </div>;
}
export function LogoShowcasePage({
  onNavigate
}: LogoShowcasePageProps) {
  const variants: Array<{
    id: 1 | 2 | 3 | 4;
    name: string;
  }> = [{
    id: 1,
    name: 'Dark (Primary)'
  }, {
    id: 2,
    name: 'Light'
  }, {
    id: 3,
    name: 'Teal Mono'
  }, {
    id: 4,
    name: 'Minimal Black'
  }];
  return <div className="min-h-screen bg-white" style={{
    fontFamily: "'Montserrat', sans-serif"
  }} data-id="element-581">
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" data-id="element-582" />

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50" data-id="element-583">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between" data-id="element-584">
          <button onClick={() => onNavigate('home')} className="inline-flex items-center gap-2 text-[#0F1E3C] hover:text-[#00C2D4] transition-colors text-sm font-medium" data-id="element-585">
            <ArrowLeftIcon size={18} data-id="element-586" />
            Вернуться на сайт
          </button>
          <span className="text-gray-400 text-sm" data-id="element-587">Brand Guidelines</span>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#F8FBFF] to-white" data-id="element-588">
        <div className="container mx-auto px-6 text-center" data-id="element-589">
          <h1 className="text-[#0F1E3C] mb-4" style={{
          fontWeight: 800,
          fontSize: 'clamp(32px, 5vw, 56px)'
        }} data-id="element-590">
            DENTOS Brand Identity
          </h1>
          <p className="text-gray-500 text-lg max-w-xl mx-auto" data-id="element-591">
            Профессиональная платформа знаний для владельцев стоматологий
            Казахстана
          </p>
        </div>
      </section>

      {/* Logo Grid Section */}
      <section className="py-16 bg-white" data-id="element-592">
        <div className="container mx-auto px-6" data-id="element-593">
          <h2 className="text-[#0F1E3C] text-center mb-4" style={{
          fontWeight: 700,
          fontSize: '28px'
        }} data-id="element-594">
            4 Color Variants × 2 Layouts
          </h2>
          <p className="text-gray-500 text-center mb-12 max-w-lg mx-auto" data-id="element-595">
            Каждый вариант логотипа оптимизирован для разных контекстов
            использования
          </p>

          {/* Grid Header */}
          <div className="grid grid-cols-3 gap-4 mb-6 max-w-5xl mx-auto" data-id="element-596">
            <div data-id="element-597"></div>
            <div className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider" data-id="element-598">
              Option A — Horizontal
            </div>
            <div className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider" data-id="element-599">
              Option B — Stacked
            </div>
          </div>

          {/* Grid Rows */}
          <div className="space-y-6 max-w-5xl mx-auto" data-id="element-600">
            {variants.map(v => <div key={v.id} className="grid grid-cols-3 gap-4 items-center" data-id="element-601">
                {/* Label */}
                <div className="text-right pr-4" data-id="element-602">
                  <span className="text-sm font-semibold text-[#0F1E3C]" data-id="element-603">
                    Variant {v.id}
                  </span>
                  <br data-id="element-604" />
                  <span className="text-xs text-gray-500" data-id="element-605">{v.name}</span>
                </div>

                {/* Horizontal */}
                <div className="flex items-center justify-center p-8 rounded-2xl" style={{
              backgroundColor: v.id === 4 ? '#F3F4F6' : '#F8FBFF'
            }} data-id="element-606">
                  <DentosLogo variant={v.id} layout="horizontal" width={280} data-id="element-607" />
                </div>

                {/* Stacked */}
                <div className="flex items-center justify-center p-8 rounded-2xl" style={{
              backgroundColor: v.id === 4 ? '#F3F4F6' : '#F8FBFF'
            }} data-id="element-608">
                  <DentosLogo variant={v.id} layout="stacked" width={180} showTagline data-id="element-609" />
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Size Scale Section */}
      <section className="py-16 bg-[#F8FBFF]" data-id="element-610">
        <div className="container mx-auto px-6" data-id="element-611">
          <h2 className="text-[#0F1E3C] text-center mb-4" style={{
          fontWeight: 700,
          fontSize: '28px'
        }} data-id="element-612">
            Size Scale
          </h2>
          <p className="text-gray-500 text-center mb-12" data-id="element-613">
            Логотип сохраняет читаемость на всех размерах
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-12" data-id="element-614">
            {/* Full */}
            <div className="text-center" data-id="element-615">
              <div className="mb-4 p-6 bg-white rounded-2xl shadow-sm" data-id="element-616">
                <DentosLogo variant={1} layout="horizontal" width={400} data-id="element-617" />
              </div>
              <span className="text-sm text-gray-500" data-id="element-618">Full — 400px</span>
            </div>

            {/* Medium */}
            <div className="text-center" data-id="element-619">
              <div className="mb-4 p-6 bg-white rounded-2xl shadow-sm" data-id="element-620">
                <DentosLogo variant={1} layout="horizontal" width={200} data-id="element-621" />
              </div>
              <span className="text-sm text-gray-500" data-id="element-622">Medium — 200px</span>
            </div>

            {/* Small / Favicon */}
            <div className="text-center" data-id="element-623">
              <div className="mb-4 p-6 bg-white rounded-2xl shadow-sm" data-id="element-624">
                <SymbolOnly variant={1} size={48} data-id="element-625" />
              </div>
              <span className="text-sm text-gray-500" data-id="element-626">Favicon — 48px</span>
            </div>
          </div>
        </div>
      </section>

      {/* Usage on Backgrounds */}
      <section className="py-16 bg-white" data-id="element-627">
        <div className="container mx-auto px-6" data-id="element-628">
          <h2 className="text-[#0F1E3C] text-center mb-4" style={{
          fontWeight: 700,
          fontSize: '28px'
        }} data-id="element-629">
            Usage on Backgrounds
          </h2>
          <p className="text-gray-500 text-center mb-12" data-id="element-630">
            Правильное использование на различных фонах
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-631">
            {/* Dark Navy Background */}
            <div className="rounded-2xl p-12 flex items-center justify-center" style={{
            backgroundColor: '#0F1E3C'
          }} data-id="element-632">
              <DentosLogo variant={1} layout="horizontal" width={240} data-id="element-633" />
            </div>

            {/* White Background */}
            <div className="rounded-2xl p-12 flex items-center justify-center border border-gray-200" style={{
            backgroundColor: '#FFFFFF'
          }} data-id="element-634">
              <DentosLogo variant={2} layout="horizontal" width={240} data-id="element-635" />
            </div>

            {/* Teal Background */}
            <div className="rounded-2xl p-12 flex items-center justify-center" style={{
            backgroundColor: '#00C2D4'
          }} data-id="element-636">
              <DentosLogo variant={3} layout="horizontal" width={240} data-id="element-637" />
            </div>
          </div>
        </div>
      </section>

      {/* Symbol Breakdown */}
      <section className="py-16 bg-[#0F1E3C]" data-id="element-638">
        <div className="container mx-auto px-6" data-id="element-639">
          <h2 className="text-white text-center mb-4" style={{
          fontWeight: 700,
          fontSize: '28px'
        }} data-id="element-640">
            Symbol Concept
          </h2>
          <p className="text-white/60 text-center mb-12 max-w-lg mx-auto" data-id="element-641">
            Геометрический символ объединяет букву D, профиль зубной дуги и
            график роста
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-12" data-id="element-642">
            <div className="text-center" data-id="element-643">
              <div className="mb-4 p-8 bg-white/10 rounded-2xl inline-block" data-id="element-644">
                <LogoSymbol color="#00C2D4" size={120} data-id="element-645" />
              </div>
              <p className="text-white/80 text-sm max-w-xs" data-id="element-646">
                Вертикальная линия + 3 восходящих столбца = буква D
              </p>
            </div>
            <div className="text-center" data-id="element-647">
              <div className="mb-4 p-8 bg-white/10 rounded-2xl inline-block" data-id="element-648">
                <LogoSymbol color="#00C2D4" size={120} data-id="element-649" />
              </div>
              <p className="text-white/80 text-sm max-w-xs" data-id="element-650">
                Форма дуги = профиль зубного ряда
              </p>
            </div>
            <div className="text-center" data-id="element-651">
              <div className="mb-4 p-8 bg-white/10 rounded-2xl inline-block" data-id="element-652">
                <LogoSymbol color="#00C2D4" size={120} data-id="element-653" />
              </div>
              <p className="text-white/80 text-sm max-w-xs" data-id="element-654">
                Восходящие столбцы = рост и данные
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <footer className="py-8 bg-white border-t border-gray-100" data-id="element-655">
        <div className="container mx-auto px-6 text-center" data-id="element-656">
          <p className="text-gray-400 text-sm" data-id="element-657">
            <span className="font-semibold text-gray-600" data-id="element-658">
              Montserrat ExtraBold 800
            </span>
            {' · '}
            Letter spacing 3px
            {' · '}
            <span style={{
            color: '#00C2D4'
          }} data-id="element-659">
              #00C2D4
            </span>
            {' · '}
            <span style={{
            color: '#0F1E3C'
          }} data-id="element-660">
              #0F1E3C
            </span>
          </p>
          <p className="text-gray-300 text-xs mt-2" data-id="element-661">
            © 2026 DENTOS. Brand Identity Guidelines.
          </p>
        </div>
      </footer>
    </div>;
}